import { trim } from './format';

/**
 * Estimate display mode. Estimates are stored as a three-point band (optimistic / most-likely
 * / pessimistic). The user picks which figure to anchor on globally:
 *   - optimistic  → the low end (min)
 *   - expected    → PERT expected = (min + 4*likely + max)/6  (default)
 *   - pessimistic → the high end (max)
 */
export type EstimateMode = 'optimistic' | 'expected' | 'pessimistic';

export const ESTIMATE_MODES: EstimateMode[] = ['optimistic', 'expected', 'pessimistic'];

export const ESTIMATE_MODE_LABELS: Record<EstimateMode, string> = {
  optimistic: 'Optimistic',
  expected: 'Expected',
  pessimistic: 'Pessimistic',
};

/**
 * Estimates are model output, not measured time, so display them on a coarse 0.05 h grid —
 * "1.1 h", not "1.12 h" — to avoid false precision. Actual logged hours are never rounded.
 */
function round05(n: number): number {
  return Math.round(n / 0.05) * 0.05;
}

/** PERT expected value. Falls back to the band midpoint when the most-likely point is absent. */
export function pertExpected(
  min: number | null | undefined,
  likely: number | null | undefined,
  max: number | null | undefined,
): number | null {
  if (min == null || max == null) return null;
  const m = likely ?? (min + max) / 2;
  return (min + 4 * m + max) / 6;
}

/** The single hours figure to show for the chosen mode, or null when there's no band. */
export function estimatePoint(
  min: number | null | undefined,
  likely: number | null | undefined,
  max: number | null | undefined,
  mode: EstimateMode,
): number | null {
  if (min == null || max == null) return null;
  if (mode === 'optimistic') return min;
  if (mode === 'pessimistic') return max;
  return pertExpected(min, likely, max);
}

/** Mode point formatted as "5 h" (rounded), or "—" when no estimate. */
export function formatEstimate(
  min: number | null | undefined,
  likely: number | null | undefined,
  max: number | null | undefined,
  mode: EstimateMode,
): string {
  const v = estimatePoint(min, likely, max, mode);
  return v == null ? '—' : `${trim(round05(v))} h`;
}

/** The optimistic→pessimistic band, shown as muted secondary context next to the point. */
export function formatBand(
  min: number | null | undefined,
  max: number | null | undefined,
): string {
  if (min == null || max == null) return '';
  const lo = round05(min);
  const hi = round05(max);
  if (Math.abs(lo - hi) < 0.05) return `${trim(lo)} h`;
  return `${trim(lo)}–${trim(hi)} h`;
}
